import { HandwritingSource } from "./handwriting-source.js";
import { tasteGate } from "./lianxi-gate.js";
import { characterItems, keptPlace, recorderOf } from "./lianxi-block-data.js";
import { fetchCharacters } from "./lianxi-exercise-characters.js";
import { XieziExercise } from "./lianxi-xiezi.js";
import { loadPlace, wantsPlace } from "./lianxi-place.js";
import { localMarker } from "./xiezi-check.js";
import { drillParts, part, said, wireControls } from "./xiezi-parts.js";
import {
  type DrillSounds,
  type SoundsFor,
  XieziSounds,
} from "./xiezi-sounds.js";
import { XieziSquare } from "./xiezi-square.js";

/**
 * Starting the writing drill Exercise on a `/lianxi/xiezi/` page.
 *
 * The block is open on the page, so the square has its size and the outlines
 * are fetched at once, one small file per character. A character whose file
 * cannot be fetched is left out of the Exercise, and a block with none left is
 * left as the markup drew it, which says what the drill is.
 * `partials/lianxi/drill.html` is the block and `lianxi-xiezi.ts` is the
 * Exercise once it is built.
 *
 * **A signed-in reader's place is kept.** `lianxi-place.ts` asks the handler
 * for it first, writes `data-cb-lianxi-record` onto the block and each
 * character's kept stage and pass onto its item. The Exercise then starts
 * each character where the handler left it, reports each pass through
 * `lianxi-recorder.ts`, and posts the free stage's ink there to be marked. A
 * block with no place on it marks in the browser and keeps nothing.
 */

/** The block, carrying the characters and where their files are. */
const exerciseSelector = "[data-cb-lianxi-xiezi]";

/** Build the Exercise inside one block. */
async function openExercise(
  block: HTMLElement,
  fetching: typeof fetch,
  soundsFor: SoundsFor,
): Promise<void> {
  const box = part(block, "square");
  const canvas = part(block, "pad");
  const characters = await fetchCharacters(block, fetching);
  const [first] = characters;

  if (!box || !(canvas instanceof HTMLCanvasElement) || first === undefined) {
    return;
  }

  // The square tells the Exercise about each stroke, and the Exercise is
  // built around the square, so the square is handed somewhere it will be.
  const holder: { exercise?: XieziExercise } = {};
  const square = new XieziSquare({
    box,
    canvas,
    data: first.strokes,
    onStroke: (): void => {
      holder.exercise?.strokeLanded();
    },
  });
  const parts = drillParts(block, square);

  if (!parts) {
    return;
  }

  const itemOf = characterItems(block);
  const recorder = recorderOf(
    block,
    characters.map((each) => each.character),
    fetching,
  );
  const exercise = new XieziExercise({
    parts,
    items: characters.flatMap((each) => itemOf(each.character) ?? []),
    characters,
    places: characters.map((each) => keptPlace(itemOf(each.character))),
    marker:
      recorder?.mark ??
      localMarker(
        new HandwritingSource({
          url: said(block, "cbHandwriting"),
          fetch: fetching,
        }),
      ),
    keeper: recorder,
    sounds: soundsFor(block.ownerDocument),
    onTasted: tasteGate(block),
  });

  holder.exercise = exercise;
  block.dataset["cbLianxiReady"] = "";
  wireControls(block, exercise, square);
}

/**
 * Start the Exercise on every block under `root`.
 *
 * @param root - The page, or the part of it to look in.
 * @param fetching - How to fetch. The window's own unless a test says otherwise.
 * @param soundsFor - What plays the drill's sounds. The browser's own output
 * unless a test says otherwise.
 */
export function startLianxi(
  root: ParentNode,
  fetching: typeof fetch = (input, init): Promise<Response> =>
    fetch(input, init),
  soundsFor: SoundsFor = (page): DrillSounds => new XieziSounds(page),
): void {
  for (const block of root.querySelectorAll<HTMLElement>(exerciseSelector)) {
    void (
      wantsPlace(block) ? loadPlace(block, fetching) : Promise.resolve()
    ).then(async () => openExercise(block, fetching, soundsFor));
  }
}
